import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import { Theme } from '@radix-ui/themes'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { format } from 'date-fns'
import type { NextComponentType } from 'next'
import { ThemeProvider } from 'next-themes'

import '@fontsource/arimo/latin-400-italic.css'
import '@fontsource/arimo/latin-400.css'
import '@fontsource/arimo/latin-700-italic.css'
import '@fontsource/arimo/latin-700.css'
import '@fontsource/caladea/latin-400-italic.css'
import '@fontsource/caladea/latin-400.css'
import '@fontsource/caladea/latin-700-italic.css'
import '@fontsource/caladea/latin-700.css'
import '@fontsource/carlito/latin-400-italic.css'
import '@fontsource/carlito/latin-400.css'
import '@fontsource/carlito/latin-700-italic.css'
import '@fontsource/carlito/latin-700.css'
import '@fontsource/cousine/latin-400-italic.css'
import '@fontsource/cousine/latin-400.css'
import '@fontsource/cousine/latin-700-italic.css'
import '@fontsource/cousine/latin-700.css'
import '@fontsource-variable/montserrat/wght-italic.css'
import '@fontsource-variable/montserrat/wght.css'
import '@fontsource/tinos/latin-400-italic.css'
import '@fontsource/tinos/latin-400.css'
import '@fontsource/tinos/latin-700-italic.css'
import '@fontsource/tinos/latin-700.css'
import '@radix-ui/themes/styles.css'
import { AppContext, AppInitialProps, AppLayoutProps, AppProps } from 'next/app'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import { useRouter } from 'next/router'

import {
  AuthProvider,
  providerName,
  refreshSessionForFetch,
  useAuthInfo,
} from '@sweetauth/core'

import { AnnouncementModalHost } from '@/components/announcements/AnnouncementModalHost'
import {
  SessionExpiryGuard,
  type SignedOutReason,
} from '@/components/auth/SessionExpiryGuard'
import { CommercialOnly } from '@/components/common/CommercialOnly'
import RouteProgressBar from '@/components/common/RouteProgressBar'
import { UserThemeReporter } from '@/components/common/UserThemeReporter'

import { APP_SHELL, resolveAppShell } from '@/constants/appShell'
import { HAS_IN_APP_AUTH_PAGES, LOGIN_URL } from '@/constants/auth'
import {
  API_URL,
  AUTH_URL,
  NEXT_PUBLIC_UNDER_MAINTENANCE,
  NEXT_PUBLIC_UNDER_MAINTENANCE_END_TIME,
} from '@/constants/env'
import { SUPPORT_EMAIL } from '@/constants/general'
import type { StoredTheme } from '@/constants/theme'
import { APP_THEMES, resolveForcedTheme, THEME_CLASS } from '@/constants/theme'

import '@/components/chat-v2/ui/assistant-ui.css'
import '@/components/chat-v2/ui/dot.css'
import '@/components/common/EventCalendar/index.css'

import { ApiError } from '@/context/usaspending/liveFetcher'
import '@/components/common/TextArea/RichTextArea/styles.css'
import '@/components/documents/OutlineBuilder/Section/index.css'
import '@/components/editor-v2/styles/index.css'

import '@harbour-enterprises/superdoc/style.layered.css'
import '@/components/editor-v3/styles/index.css'

import { withAuth } from '@/helpers/auth'
import { promotePendingLoginMethod } from '@/helpers/login'
import {
  RenderTimeProvider,
  getPageRenderedAt,
} from '@/helpers/ssr/render-time'
import { createKeycloakAuthShellLoginUrl } from '@/helpers/url'

import '@/extensions/UploadImage/styles.css'

import { useRenderNowMs } from '@/hooks/common/useRenderNowMs'

import { initDatadog } from '@/lib/datadog'
import { setupFetchWrapper } from '@/lib/fetch'
import { setBootstrapStoredHeader } from '@/lib/fetch/authHeader'
import {
  bootstrapAuthConsistencyWindowOnce,
  clearAuthConsistencyAccountCreatedAt,
  setAuthConsistencyAccountCreatedAt,
} from '@/lib/fetch/spiceConsistencyBootstrap'

import { appFontCss, inter } from '@/styles/fonts'

import type { NextPageWithLayout } from '@/types/next'

import { BookmarkCheckLoaderProvider } from '@/services/bookmark-check-loader/context'
import { EntitlementLoaderProvider } from '@/services/entitlement-loader/context'

import '@/styles/globals.css'

import { FilePresenceLoaderProvider } from '@/services/file-presence-loader/context'
import { PermissionLoaderProvider } from '@/services/permission-loader/context'
import { UnsavedChangesProvider } from '@/services/unsaved-changes/context'

// keycloak's BFF /auth/* endpoints live on the API origin; propelauth uses its
// hosted frontend URL. The active provider comes from sweetauth.
const SWEETSPOT_AUTH_PROVIDER_URL_PROVIDERS = {
  propelauth_cloud: AUTH_URL,
  keycloak: API_URL,
}
const SWEETSPOT_AUTH_PROVIDER_URL =
  SWEETSPOT_AUTH_PROVIDER_URL_PROVIDERS[providerName]

if (typeof window !== 'undefined') {
  const allowedOrigins = API_URL ? [API_URL] : []
  setupFetchWrapper({
    headerName: 'X-Auth-Cookie',
    allowedOrigins,
    captureStatusCodes: [400, 409, 412, 422, 428],
    // Keycloak completes the BFF: the access token is an httpOnly cookie, so drop the bearer header
    // and send credentials. PropelAuth stays on the bearer token its SDK manages.
    cookieAuth: providerName === 'keycloak',
    // On a 401 in cookie mode, refresh once (single-flighted with the guard) and replay the request.
    onUnauthorized:
      providerName === 'keycloak'
        ? () => refreshSessionForFetch(API_URL)
        : undefined,
  })

  // Bootstrap SpiceDB consistency token from URL (Slack deep-links)
  // Store the header immediately so it's available for the first fetch.
  // URL cleanup happens after Next.js hydration in useStripConsistencyKey().
  const ckParams = new URLSearchParams(window.location.search)
  const ck = ckParams.get('_ck')
  if (ck) {
    setBootstrapStoredHeader(ck)
  }
}

const DynamicSidebar = dynamic(() => import(`@/components/common/Sidebar`), {
  ssr: false,
})
const DynamicToast = dynamic(() => import('@/components/common/Toast'), {
  ssr: false,
})
// an easter egg has no business in the first paint, and it renders nothing
// until a key sequence lands anyway
const DynamicBananaRain = dynamic(
  () => import('@/components/common/BananaRain'),
  { ssr: false },
)

interface WithChildrenProps {
  children: React.ReactNode
}

// Knock provider that always renders children immediately.
// Uses manual dynamic import to avoid the loading state blocking children.
// This keeps Knock at the top level to persist across page navigations (avoiding rate limits).
const CommercialKnockProvider: React.FC<WithChildrenProps> = ({ children }) => {
  // Use ref for the component (avoids the quirk of storing functions in state)
  // and boolean state just to trigger re-render when loaded
  const knockProviderRef =
    useRef<React.ComponentType<WithChildrenProps> | null>(null)

  const [isLoaded, setIsLoaded] = useState<boolean>(false)

  useEffect(() => {
    // If the component unmounts before the import resolves, setIsLoaded(true) will be called on an unmounted component
    let mounted: boolean = true
    import('@/notifications')
      .then((mod) => {
        if (mounted) {
          knockProviderRef.current = mod.NotificationProvider
          setIsLoaded(true)
        }
      })
      .catch((err) => {
        // Log but don't crash - children render without Knock (graceful degradation)
        console.error('Failed to load KnockProvider:', err)
      })
    return () => {
      mounted = false
    }
  }, [])

  // Children render at once rather than behind a loading screen — but the
  // provider appearing above them when the import lands remounts them.
  if (isLoaded && knockProviderRef.current) {
    const Provider = knockProviderRef.current
    return <Provider>{children}</Provider>
  }

  return <>{children}</>
}

const ClientOnlyKnockProvider: React.FC<WithChildrenProps> = ({ children }) => (
  <CommercialOnly fallback={children}>
    <CommercialKnockProvider>{children}</CommercialKnockProvider>
  </CommercialOnly>
)

// Only load ReactQueryDevtools in development
const ReactQueryDevtools =
  process.env.NODE_ENV === 'development'
    ? dynamic(
        () =>
          import('@tanstack/react-query-devtools').then(
            (mod) => mod.ReactQueryDevtools,
          ),
        { ssr: false },
      )
    : () => null

const useClientLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect

// Check if an error is a ServiceWakingError (ClickHouse waking up)
function isServiceWakingError(
  error: unknown,
): error is { status: number; retryAfter: number; retryReason: string } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    error.status === 503 &&
    'retryReason' in error &&
    error.retryReason === 'clickhouse-waking'
  )
}

const CLICKHOUSE_WAKE_MAX_RETRIES = 60

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: (failureCount, error) => {
          if (isServiceWakingError(error))
            return failureCount < CLICKHOUSE_WAKE_MAX_RETRIES
          if (error instanceof ApiError && error.status === 404) return false
          return failureCount < 3
        },
        retryDelay: (failureCount, error) => {
          if (isServiceWakingError(error)) return error.retryAfter * 1000
          return Math.min(1000 * 2 ** failureCount, 30000)
        },
      },
    },
  })
}

let browserQueryClient: QueryClient | undefined

function getQueryClient() {
  if (typeof window === 'undefined') return makeQueryClient()

  browserQueryClient ??= makeQueryClient()
  return browserQueryClient
}

const getMaintenanceEndTime = (
  referenceNowMs: number | null,
): string | null => {
  if (!NEXT_PUBLIC_UNDER_MAINTENANCE_END_TIME || referenceNowMs == null)
    return null
  try {
    const endTime = new Date(NEXT_PUBLIC_UNDER_MAINTENANCE_END_TIME)
    if (referenceNowMs > endTime.getTime()) return null
    return format(endTime, "EEEE, MMMM do 'at' h:mm a")
  } catch {
    return null
  }
}

const UnderMaintenance: React.FC = () => {
  const referenceNowMs = useRenderNowMs()
  const endTime = getMaintenanceEndTime(referenceNowMs)
  return (
    <div className="flex flex-col items-center justify-center h-screen gap-6 bg-white dark:bg-default-dark px-4">
      <span className="text-5xl animate-bounce">🛠️</span>
      <h1 className="text-3xl font-bold text-accent-500 dark:text-accent-400">
        We&rsquo;ll Be Right Back!
      </h1>
      <p className="text-lg max-w-[600px] text-center text-primary-700 dark:text-primary-300">
        We&rsquo;re giving Sweetspot a little tune-up as part of scheduled
        maintenance.
        <br />
        {endTime ? (
          <>
            Please check back after <b>{endTime}</b>.<br />
          </>
        ) : (
          <>
            Please check back soon.
            <br />
          </>
        )}
        <span className="inline-block mt-2">Thank you for your patience!</span>
      </p>
      <span className="text-3xl mt-2">☕ 🦥</span>
      <a
        href={`mailto:${SUPPORT_EMAIL}`}
        className="text-sm text-blue-600 dark:text-blue-400 hover:underline underline-offset-[3px]"
      >
        Contact us
      </a>
    </div>
  )
}

const AuthLoginMethodPersistence: React.FC = () => {
  const { isLoggedIn, loading, user } = useAuthInfo()

  useClientLayoutEffect(() => {
    if (loading) {
      return
    }

    if (!isLoggedIn) {
      clearAuthConsistencyAccountCreatedAt()
      return
    }

    setAuthConsistencyAccountCreatedAt(user?.createdAt)
  }, [isLoggedIn, loading, user?.createdAt, user?.userId])

  useEffect(() => {
    if (loading || !isLoggedIn) {
      return
    }

    promotePendingLoginMethod()
  }, [isLoggedIn, loading])

  return null
}

/** Strip _ck from the URL after Next.js router hydration so it doesn't reappear. */
function useStripConsistencyKey() {
  const router = useRouter()
  const didStrip = useRef(false)

  useEffect(() => {
    if (didStrip.current || !router.isReady) return
    if (!router.query._ck) return

    didStrip.current = true
    const { _ck, ...rest } = router.query
    router.replace({ pathname: router.pathname, query: rest }, undefined, {
      shallow: true,
    })
    // oxlint-disable-next-line react/exhaustive-deps
  }, [router.isReady, router.query._ck, router.pathname])
}

const MyApp: NextComponentType<AppContext, AppInitialProps, AppLayoutProps> = ({
  Component,
  pageProps,
}: AppProps & { Component: NextPageWithLayout }) => {
  const [queryClient] = useState(getQueryClient)

  // Session-ended reason, owned here — the common parent of AuthProvider and SessionExpiryGuard
  // — so the auth store's mid-session 401 (via onUnauthenticated) and the guard's idle/cap
  // watchdog feed one source of truth. First reason wins.
  const [signedOutReason, setSignedOutReason] =
    useState<SignedOutReason | null>(null)

  // A page that only has a design in some of the themes declares them, and
  // anything else is pinned to the first one it names. The user's pick can only
  // be read from inside the provider, so it arrives here a render late — see
  // UserThemeReporter — and undefined until it does, which is what the server
  // and the render that hydrates it resolve against.
  //
  // The reporter is mounted for every page, not only the ones that declare a
  // list. It is what keeps this value current, so leaving it off the other pages
  // would let the pick go stale there and pin the next declaring page against a
  // theme the user has since changed — arriving on the editor already dark, or
  // pinned away from a professional they had just chosen.
  const [userTheme, setUserTheme] = useState<StoredTheme | undefined>(undefined)
  const supportedThemes = Component.supportedThemes
  const forcedTheme = resolveForcedTheme(supportedThemes, userTheme)

  const signalSessionEnded = useCallback(
    (reason: SignedOutReason) =>
      setSignedOutReason((current) => current ?? reason),
    [],
  )
  const handleUnauthenticated = useCallback(
    () => signalSessionEnded('expired'),
    [signalSessionEnded],
  )

  const authConsistencyBootstrapWindowMs =
    Component.authConsistencyBootstrapWindowMs
  const authConsistencyBootstrapKey =
    typeof window !== 'undefined' && authConsistencyBootstrapWindowMs
      ? `${window.location.pathname}:${authConsistencyBootstrapWindowMs}`
      : null

  bootstrapAuthConsistencyWindowOnce(
    authConsistencyBootstrapKey,
    authConsistencyBootstrapWindowMs,
  )
  useStripConsistencyKey()

  const appShell = resolveAppShell(Component.appShell)
  const isExtensionAuthShell = appShell === APP_SHELL.extensionAuth
  const rendersSharedAppChrome = appShell === APP_SHELL.app
  const rendersAppProviders =
    rendersSharedAppChrome || appShell === APP_SHELL.noChrome
  const isAuthShell = appShell === APP_SHELL.auth

  const renderedAt = getPageRenderedAt(pageProps)

  useEffect(() => {
    if (typeof window !== 'undefined' && !isExtensionAuthShell) {
      initDatadog()
    }
  }, [isExtensionAuthShell])

  // When the active provider has no in-app /auth/* UI (keycloak — those pages are
  // PropelAuth's hosted-flow surface that throws without a PropelAuth AuthContext),
  // bounce to the provider's login URL (the server_v2 BFF) rather than mounting the page.
  // The pages are also excluded from the build itself in next.config.
  useEffect(() => {
    if (isAuthShell && !HAS_IN_APP_AUTH_PAGES) {
      window.location.href = createKeycloakAuthShellLoginUrl(
        LOGIN_URL,
        window.location.search,
      )
    }
  }, [isAuthShell])

  if (isAuthShell && !HAS_IN_APP_AUTH_PAGES) {
    return null
  }

  return (
    <>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <style>{appFontCss}</style>
      </Head>
      <RenderTimeProvider renderedAt={renderedAt}>
        <ThemeProvider
          themes={APP_THEMES}
          value={THEME_CLASS}
          forcedTheme={forcedTheme}
          attribute="class"
          disableTransitionOnChange
        >
          <UserThemeReporter onChange={setUserTheme} />
          {isExtensionAuthShell ? (
            <Theme className={inter.className}>
              {withAuth(Component, pageProps)}
            </Theme>
          ) : (
            <AuthProvider
              authUrl={SWEETSPOT_AUTH_PROVIDER_URL}
              onUnauthenticated={handleUnauthenticated}
            >
              <AuthLoginMethodPersistence />
              <SessionExpiryGuard
                signedOutReason={signedOutReason}
                onSessionEnded={signalSessionEnded}
              />
              {NEXT_PUBLIC_UNDER_MAINTENANCE ? (
                <UnderMaintenance />
              ) : (
                <PermissionLoaderProvider>
                  <EntitlementLoaderProvider>
                    <QueryClientProvider client={queryClient}>
                      <FilePresenceLoaderProvider>
                        <ReactQueryDevtools initialIsOpen={false} />
                        <Theme className={inter.className}>
                          <RouteProgressBar />
                          <UnsavedChangesProvider>
                            {rendersAppProviders ? (
                              // Gated apart from the chrome: a page that drops
                              // out of these remounts everything under them.
                              <BookmarkCheckLoaderProvider>
                                <ClientOnlyKnockProvider>
                                  <>
                                    {withAuth(Component, pageProps)}
                                    {rendersSharedAppChrome ? (
                                      <>
                                        <DynamicSidebar />
                                        <CommercialOnly>
                                          <AnnouncementModalHost />
                                        </CommercialOnly>
                                      </>
                                    ) : null}
                                  </>
                                </ClientOnlyKnockProvider>
                              </BookmarkCheckLoaderProvider>
                            ) : (
                              withAuth(Component, pageProps)
                            )}
                          </UnsavedChangesProvider>
                          <DynamicToast />
                          <DynamicBananaRain />
                        </Theme>
                      </FilePresenceLoaderProvider>
                    </QueryClientProvider>
                  </EntitlementLoaderProvider>
                </PermissionLoaderProvider>
              )}
            </AuthProvider>
          )}
        </ThemeProvider>
      </RenderTimeProvider>
    </>
  )
}

export default MyApp
